import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import Button from '@/common/ui/components/Button';
import Input from '@/common/ui/components/Input';

interface LoginFormProps {
  onError?: (error: string) => void;
  disableSignup?: boolean;
}

export default function LoginForm({ onError, disableSignup = false }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn, signUp } = useAuth();

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return 'An unknown error occurred';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp && !disableSignup) {
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md w-full space-y-8">
      <div>
        <h2 className="mt-6 text-center text-display font-inter text-text-primary">
          {isSignUp && !disableSignup ? 'Create your account' : 'Sign in to your account'}
        </h2>
        <p className="mt-2 text-center text-body text-text-secondary font-inter">
          CampusMeds - Medication Management for College Students
        </p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="bg-error-red/10 border border-error-red/20 rounded-md p-3">
            <p className="text-supporting text-error-red font-inter">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <Input
            id="email"
            name="email"
            type="email"
            label="Email address"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
          />

          <Input
            id="password"
            name="password"
            type="password"
            label="Password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
          />
        </div>

        <div>
          <Button
            type="submit"
            loading={loading}
            fullWidth
            variant="primary"
          >
            {isSignUp && !disableSignup ? 'Create Account' : 'Sign In'}
          </Button>
        </div>

        {!disableSignup && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-supporting text-trust-blue hover:text-trust-blue/80 transition-colors duration-200 font-inter"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}